import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { DocusignTemplateActionsCell } from 'src/components/docusign-template/grid/DocusignTemplateActionsCell'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import { docusignTemplateTableHeaderTitles } from './constants'
import { DocusignTemplatesTableFields } from './enums'

/**
 * Wiring only — date handling comes from utils/grid, actions from DocusignTemplateActionsCell.
 *
 * A factory rather than a module constant: the actions column closes over the page's delete
 * handler — and over nothing else. `onDeleteTemplate` is expected to be a stable `useCallback`
 * reference, so the memoized result stays referentially stable for the life of the page. The id
 * currently being deleted is read by DocusignTemplateActionsCell itself, precisely so this array
 * does not have to be rebuilt mid-delete: a fresh array resets the grid's column widths and
 * order. Callers must still memoize the result.
 */
export const buildDocusignTemplateColumns = (
  onDeleteTemplate: (template: DocusignTemplate) => void,
  // `docusign_templates_manage` (ENG-1207 part 2). Delete is the only live row action — Edit is a
  // disabled stub — so without it the actions column is dropped.
  canManage: boolean,
): Array<GridColDef<DocusignTemplate>> => {
  const columns: Array<GridColDef<DocusignTemplate>> = [
    {
      field: DocusignTemplatesTableFields.title,
      headerName: docusignTemplateTableHeaderTitles[DocusignTemplatesTableFields.title],
      flex: 2,
      minWidth: 200,
      sortable: true,
      // Title identifies the row, so it is never offered for hiding in the manage-columns panel.
      hideable: false,
      // v8 renders cells in text mode by default; element children are only laid out (and
      // vertically centred) when the column opts into flex display.
      display: 'flex',
      renderCell: (params: GridRenderCellParams<DocusignTemplate>) => {
        const title = params.row.title?.trim()

        if (!title) {
          // `title` is nullable in the DB even though the create DTO requires it, so older rows
          // can arrive without one. A dash is a cosmetic default, not fabricated content.
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {title}
          </Typography>
        )
      },
    },
    {
      field: DocusignTemplatesTableFields.description,
      headerName: docusignTemplateTableHeaderTitles[DocusignTemplatesTableFields.description],
      flex: 3,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<DocusignTemplate>) => {
        const description = params.row.description?.trim()

        if (!description) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {description}
          </Typography>
        )
      },
    },
    {
      field: DocusignTemplatesTableFields.docusignTemplateId,
      headerName:
        docusignTemplateTableHeaderTitles[DocusignTemplatesTableFields.docusignTemplateId],
      flex: 2,
      minWidth: 200,
      sortable: true,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<DocusignTemplate>) => {
        // Matches how insuranceProductGrid and lmsTrainingGrid render an id: a GUID does not
        // fit the column at a readable size, so it truncates, and the native `title` attribute
        // keeps the full value recoverable on hover without changing the row height.
        return (
          <Typography variant="body2" css={tw`truncate`} title={params.row.docusignTemplateId}>
            {params.row.docusignTemplateId}
          </Typography>
        )
      },
    },
    {
      field: DocusignTemplatesTableFields.createdAt,
      headerName: docusignTemplateTableHeaderTitles[DocusignTemplatesTableFields.createdAt],
      width: 160,
      sortable: true,
      type: 'date',
      // Backend `createdAt` is unix SECONDS — parse first, then format the resulting Date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: DocusignTemplatesTableFields.actions,
      headerName: docusignTemplateTableHeaderTitles[DocusignTemplatesTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<DocusignTemplate>) => {
        return <DocusignTemplateActionsCell template={params.row} onDelete={onDeleteTemplate} />
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== DocusignTemplatesTableFields.actions
  })
}
